import { PROJECT_SRV } from '../../api/config';
import getFormatMessage from '../../locales/get-format-message';
import { AccountType, IFetchAllOptions, IProxyRequestBody } from '../../model';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../default-page';
import { urlWithQueryString } from '../url';
import { getGCloudAPIEndpoint } from './endpoint';
import {
  blobRequest,
  HTTPMethod,
  parsedResponse,
  parsedTGitResponse,
  request,
} from './http';
import { formatReqParams, getReformPath } from './lib';

const getEndpoint = (type: AccountType) => {
  switch (type) {
    case AccountType.auto:
      return getGCloudAPIEndpoint();
    case AccountType.Cross:
      return '';
    default:
      return getGCloudAPIEndpoint();
  }
};

export async function withAPIAccount<T>(
  type: AccountType,
  fn: (api: API) => Promise<T>
) {
  const endpoint = getEndpoint(type);
  if (endpoint === null) {
    throw new Error(getFormatMessage('please_sign_in_first'));
  }
  const api = new API(endpoint);
  return fn(api);
}

export class API {
  private privateEndpoint: string;
  public constructor(endpoint: string) {
    this.privateEndpoint = endpoint;
  }

  public async commonRequest(
    method: HTTPMethod,
    path: string,
    body?: Object,
    customHeaders?: Object,
    timeout?: number
  ): Promise<Response> {
    const formatPath = getReformPath(path);

    return await request(
      this.privateEndpoint,
      method,
      formatPath,
      body,
      {
        ...(customHeaders || {}),
      },
      timeout || 60000
    );
  }

  public async blobCommonRequest(
    method: HTTPMethod,
    path: string,
    body?: Object,
    customHeaders?: Object,
    timeout?: number
  ): Promise<ArrayBuffer> {
    return await blobRequest(
      this.privateEndpoint,
      method,
      path,
      body,
      {
        ...(customHeaders || {}),
      },
      timeout || 60000
    );
  }

  // 后台请求转发的入口
  public async proxyRequest(
    projectID: string,
    platformID: number,
    transparentReqBody: IProxyRequestBody,
    customHeaders?: Object,
    timeout?: number
  ): Promise<Response> {
    const url = `${PROJECT_SRV}/PlatformProxy`;
    let proxyPath = transparentReqBody.path;

    if (transparentReqBody.query) {
      const params: {
        [key: string]: string;
      } = {};
      Object.assign(params, transparentReqBody.query);
      proxyPath = urlWithQueryString(proxyPath, params);
    }

    const body = transparentReqBody.body || {};
    return await request(
      this.privateEndpoint,
      'POST',
      url,
      body,
      {
        'NextGameDev-Project-ID': projectID,
        'NextGameDev-Platform-ID': platformID,
        'PlatformProxy-Method': transparentReqBody.method,
        'PlatformProxy-Path': proxyPath,
        ...(customHeaders || {}),
      },
      timeout
    );
  }

  public async proxyFetchAll<T>(
    projectID: string,
    platformID: number,
    transparentReqBody: IProxyRequestBody,
    reqType: AccountType,
    options?: IFetchAllOptions<T>
  ) {
    const buf = new Array<T>();
    const opts: IFetchAllOptions<T> = {
      perPage: DEFAULT_PAGE_SIZE,
      ...options,
    };
    const params = { per_page: `${opts.perPage}` };
    const sourcePath = transparentReqBody.path;

    let nextPath: string | null = urlWithQueryString(
      sourcePath,
      formatReqParams(params, reqType)
    );
    let page = DEFAULT_PAGE;

    do {
      Object.assign(transparentReqBody, {
        path: nextPath,
      });
      const response: Response = await this.proxyRequest(
        projectID,
        platformID,
        transparentReqBody,
        {},
        options?.timeout
      );

      if (
        opts.suppressErrors !== false &&
        reqType === AccountType.UGit &&
        !response.ok
      ) {
        return buf;
      }

      let items: ReadonlyArray<T> = [];
      switch (reqType) {
        case AccountType.TAPD:
          items = await parsedResponse<ReadonlyArray<T>>(response, 'data');
          break;
        default:
          items = await parsedTGitResponse<ReadonlyArray<T>>(response);
      }

      if (items) {
        buf.push(...items);
      }

      if (opts.continue) {
        if (!opts.continue(buf)) {
          return buf;
        }
      }

      if (items.length < Number(params.per_page)) {
        nextPath = null;
      } else {
        page = page + 1;
        nextPath = urlWithQueryString(
          sourcePath,
          formatReqParams(
            {
              page,
              per_page: params.per_page,
            },
            reqType
          )
        );
      }
    } while (nextPath);

    return buf;
  }

  public get endpoint() {
    return this.privateEndpoint;
  }
}
